// WOS-BUNDLE in ⌘K (Work OS palette: recents on an empty query). Rendered before the search
// hits (palette-hits.tsx):
//   - an empty query lists the person's recents from the server ("Recently opened": the tasks,
//     docs, boards and spaces they last opened, newest first);
//   - while a task is open (`?task=`), "Link to task…" opens the drawer's link picker, and a typed
//     query offers matching tasks as link targets ("Link to #12 …", a `relates_to` link).
import { CommandGroup, CommandItem, toast } from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  CheckSquareIcon,
  FileTextIcon,
  LayersIcon,
  LinkIcon,
  type LucideIcon,
  SquareKanbanIcon,
} from "lucide-react";
import type { Api } from "../api/client";
import type { CompanyAccess } from "../features/company";
import { useOperatorOnly } from "../features/operator-only";
import { type RecentView, recentsQuery } from "../features/work/recents";
import { LINK_EVENT, linksKey } from "../features/work/task-links";
import { taskSearchQuery, workKeys } from "../features/work/work-queries";

const RECENT_ICON: Record<RecentView["kind"], LucideIcon> = {
  space: LayersIcon,
  board: SquareKanbanIcon,
  page: FileTextIcon,
  task: CheckSquareIcon,
};

/** The palette pair (`kind:id`, palette-recents.ts) a server recent is, to skip it below. */
export function recentPair(r: Pick<RecentView, "kind" | "id">): string {
  return `${r.kind === "page" ? "doc" : r.kind}:${r.id}`;
}

/** Where a recent opens. */
export function recentTarget(
  r: Pick<RecentView, "kind" | "id">,
  slug: string,
): { to: string; params: Record<string, string>; search?: Record<string, string> } {
  switch (r.kind) {
    case "space":
      return { to: "/c/$slug/work/s/$spaceId", params: { slug, spaceId: r.id } };
    case "board":
      return { to: "/c/$slug/work/b/$boardId", params: { slug, boardId: r.id } };
    case "page":
      return { to: "/c/$slug/work/p/$pageId", params: { slug, pageId: r.id } };
    case "task":
      return { to: "/c/$slug/work", params: { slug }, search: { task: r.id } };
  }
}

export function WorkPaletteExtras({
  api,
  company,
  query,
  onPick,
}: {
  api: Api;
  company: CompanyAccess;
  query: string;
  onPick: (fn: () => void) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const openTask = useRouterState({
    select: (s) => (s.location.search as { task?: unknown }).task,
  });
  const taskId = typeof openTask === "string" ? openTask : null;
  const q = query.trim();
  const operatorOnly = useOperatorOnly(api, co);
  const recents = useQuery({ ...recentsQuery(api, co), enabled: q === "" && !operatorOnly });
  const hits = useQuery({
    ...taskSearchQuery(api, co, q),
    enabled: Boolean(taskId) && q.length >= 2,
  });

  const link = (target: { id: string; number: number; title: string }) => {
    if (!taskId) return;
    void api.client.tasks
      .addLink({ params: { co, id: taskId }, body: { relation: "relates_to", task_id: target.id } })
      .then(
        () => {
          toast.add({
            title: `Linked to #${target.number}`,
            description: target.title,
            type: "success",
          });
          void qc.invalidateQueries({ queryKey: linksKey(co, taskId) });
          void qc.invalidateQueries({ queryKey: workKeys.task(co, target.id) });
        },
        () => toast.add({ title: "The link could not be added", type: "warning" }),
      );
  };

  const recentRows = q === "" ? (recents.data ?? []) : [];
  const targets =
    taskId && q.length >= 2
      ? (hits.data?.data ?? []).filter((t) => t.id !== taskId).slice(0, 5)
      : [];
  return (
    <>
      {taskId ? (
        <CommandGroup heading="This task">
          <CommandItem
            value="Link to task… link relate duplicate block"
            onSelect={() =>
              onPick(() => window.dispatchEvent(new CustomEvent(LINK_EVENT, { detail: taskId })))
            }
          >
            <LinkIcon aria-hidden="true" />
            Link to task…
          </CommandItem>
        </CommandGroup>
      ) : null}
      {targets.length ? (
        <CommandGroup heading="Link this task to" forceMount>
          {targets.map((t) => (
            <CommandItem
              key={t.id}
              value={`link-target ${t.id}`}
              forceMount
              data-kind="link"
              onSelect={() => onPick(() => link(t))}
            >
              <LinkIcon aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">
                Link to <span className="text-muted-foreground tabular-nums">#{t.number}</span>{" "}
                {t.title}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      ) : null}
      {recentRows.length ? (
        <CommandGroup heading="Recently opened" forceMount>
          {recentRows.map((r) => {
            const Icon = RECENT_ICON[r.kind];
            return (
              <CommandItem
                key={`${r.kind}:${r.id}`}
                value={`recent ${r.kind} ${r.id}`}
                forceMount
                data-kind={`recent-${r.kind}`}
                onSelect={() =>
                  onPick(() => {
                    const t = recentTarget(r, company.slug);
                    void navigate({
                      to: t.to as never,
                      params: t.params as never,
                      ...(t.search ? { search: t.search as never } : {}),
                    });
                  })
                }
              >
                <Icon aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">
                  {r.title}
                  {r.path.length ? (
                    <span className="ml-2 text-muted-foreground">{r.path.join(" › ")}</span>
                  ) : null}
                </span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      ) : null}
    </>
  );
}
